/**
 * ChatPanel — Production-grade AI chat assistant for Avexo CA Statement Engine
 *
 * ARCHITECTURE RULES (do not violate):
 *  • All sub-components defined at MODULE LEVEL (outside ChatPanel)
 *  • Uncontrolled textarea — no value={}, no onChange=, no useState for input
 *  • ONE InputBar in the entire file, ONE panel shell (CSS switching, not two JSX trees)
 *  • messagesRef pattern — sendMessage reads history via ref, not state closure
 *  • key="avexo-ca-chat-input" — stable literal, never dynamic
 *  • No setTimeout — only requestAnimationFrame for focus
 *  • export default memo(ChatPanel) — prevents ReviewPage re-renders reaching this tree
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Briefcase,
  ChevronDown,
  Expand,
  Minimize2,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import api from '../utils/api';

// ─── Suggestion chips shown on empty state ────────────────────────────────────
const SUGGESTIONS = [
  'Monthly P&L summary',
  'GST & TDS payments',
  'Top 5 expenses',
  'Suspicious transactions?',
  'Salary vs business income',
  'Highest outflow month',
];

// ─── Shell class sets (compact vs expanded) ───────────────────────────────────
const compactShell =
  'fixed bottom-[88px] right-6 z-50 w-[390px] max-w-[calc(100vw-3rem)] ' +
  'rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 ' +
  'bg-white dark:bg-slate-900 flex flex-col overflow-hidden';

const expandedShell =
  'fixed top-0 bottom-0 right-0 z-50 w-[520px] max-w-full ' +
  'rounded-l-2xl shadow-2xl border-l-2 border-violet-300 dark:border-violet-600 ' +
  'bg-white dark:bg-slate-900 flex flex-col overflow-hidden';

// ─── TypingDots ───────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-[3px] h-4">
      {[0, 180, 360].map((delay) => (
        <span
          key={delay}
          className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce"
          style={{ animationDelay: `${delay}ms`, animationDuration: '900ms' }}
        />
      ))}
    </span>
  );
}

// ─── InlineFormat — **bold** and `code` ──────────────────────────────────────
function InlineFormat({ text }) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const raw = match[0];
    if (raw.startsWith('**')) {
      parts.push(<strong key={match.index} className="font-semibold">{raw.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <code key={match.index} className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[11px] font-mono">
          {raw.slice(1, -1)}
        </code>
      );
    }
    last = match.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// ─── RichText — bullets, numbered lists, paragraphs ──────────────────────────
function RichText({ content }) {
  const lines = content.split('\n');
  const elements = [];
  let listItems = [];

  const flushList = (key) => {
    if (listItems.length) {
      elements.push(
        <ul key={`ul-${key}`} className="list-disc list-inside space-y-0.5 my-1">
          {listItems.map((li, i) => (
            <li key={i} className="text-sm leading-snug">
              <InlineFormat text={li} />
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((line, i) => {
    const bullet = line.match(/^[•\-\*]\s+(.*)/);
    const numbered = line.match(/^\d+\.\s+(.*)/);
    if (bullet) {
      listItems.push(bullet[1]);
    } else if (numbered) {
      listItems.push(numbered[1]);
    } else {
      flushList(i);
      if (line.trim()) {
        elements.push(
          <p key={i} className="text-sm leading-snug my-0.5">
            <InlineFormat text={line} />
          </p>
        );
      }
    }
  });
  flushList('end');

  return <div className="space-y-0.5">{elements}</div>;
}

// ─── MessageBubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-[chatMsgIn_0.18s_ease-out_both]`}
    >
      {!isUser && (
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-violet-600 to-sky-500 flex items-center justify-center mr-2 mt-0.5">
          <Sparkles className="w-3 h-3 text-white" />
        </div>
      )}
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm ${
          isUser
            ? 'bg-sky-600 text-white rounded-br-sm'
            : 'bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-100 dark:border-slate-700 rounded-bl-sm'
        }`}
      >
        {isUser ? (
          <p className="text-sm leading-snug whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <RichText content={msg.content} />
        )}
      </div>
    </div>
  );
}

// ─── MessageList ──────────────────────────────────────────────────────────────
function MessageList({ messages, loading, error, onDismissError, messagesEndRef, expanded }) {
  return (
    <div
      className={`flex-1 overflow-y-auto px-3 py-3 space-y-3 ${
        expanded ? 'min-h-0' : ''
      }`}
    >
      {messages.map((msg, i) => (
        <MessageBubble key={i} msg={msg} />
      ))}

      {loading && (
        <div className="flex justify-start animate-[chatMsgIn_0.18s_ease-out_both]">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-violet-600 to-sky-500 flex items-center justify-center mr-2 mt-0.5">
            <Sparkles className="w-3 h-3 text-white" />
          </div>
          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl rounded-bl-sm px-3 py-2.5 shadow-sm">
            <TypingDots />
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-3 py-2 text-xs text-red-700 dark:text-red-400 animate-[chatMsgIn_0.18s_ease-out_both]">
          <span className="flex-1">{error}</span>
          <button
            onClick={onDismissError}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
            aria-label="Dismiss error"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────
function EmptyState({ onSend }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 gap-5">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-sky-500 flex items-center justify-center shadow-lg">
          <Briefcase className="w-6 h-6 text-white" />
        </div>
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          Your CA Assistant
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-[260px]">
          Ask anything about this statement — GST, TDS, monthly P&amp;L, suspicious
          transactions, and more.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => onSend(s)}
            className="text-left text-xs px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-all duration-100 leading-snug"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── InputBar — UNCONTROLLED ──────────────────────────────────────────────────
function InputBar({ onSend, loading, textareaRef }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleInput = (e) => {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  return (
    <div className="px-3 pb-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
      <div className="flex items-end gap-2 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-600 px-3 py-2 focus-within:ring-2 focus-within:ring-violet-400 focus-within:border-violet-400 transition-shadow duration-100">
        <textarea
          key="avexo-ca-chat-input"
          ref={textareaRef}
          rows={1}
          placeholder="Ask your CA anything about this statement…"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={loading}
          className="peer flex-1 bg-transparent text-sm resize-none focus:outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-60 py-0.5 leading-snug"
          style={{ fieldSizing: 'content', minHeight: '22px', maxHeight: '120px' }}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
        />
        <button
          onClick={onSend}
          disabled={loading}
          aria-label="Send message"
          className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-sky-500 flex items-center justify-center shadow-sm hover:opacity-90 active:scale-95 transition-all duration-100 disabled:opacity-50 disabled:cursor-not-allowed peer-placeholder-shown:opacity-40 peer-placeholder-shown:pointer-events-none"
        >
          <Send className="w-4 h-4 text-white" />
        </button>
      </div>
      <p className="text-[10px] text-slate-400 dark:text-slate-600 text-center mt-1.5 select-none">
        Enter to send · Shift+Enter for new line · Esc to close
      </p>
    </div>
  );
}

// ─── ChatPanel ────────────────────────────────────────────────────────────────
function ChatPanel({ statementId, bankName, accountNumber }) {
  const [open,     setOpen]     = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const textareaRef   = useRef(null);
  const messagesEndRef = useRef(null);
  // messagesRef mirrors messages state so sendMessage never captures stale closure
  const messagesRef   = useRef([]);

  // ── Keep messagesRef in sync ─────────────────────────────────────────────
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ── Auto-scroll on new messages / typing indicator ───────────────────────
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, open]);

  // ── Focus textarea when panel opens ─────────────────────────────────────
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open]);

  // ── Escape key closes panel ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setExpanded(false);
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // ── sendMessage — deps: [loading, statementId] ONLY ─────────────────────
  // history is read via messagesRef.current to avoid stale closures
  const sendMessage = useCallback(
    async (textArg) => {
      const el = textareaRef.current;
      const content = (
        typeof textArg === 'string' ? textArg : el?.value ?? ''
      ).trim();
      if (!content || loading) return;

      // Clear textarea (uncontrolled DOM manipulation)
      if (el) {
        el.value = '';
        el.style.height = 'auto';
      }

      const history = messagesRef.current.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      setMessages((prev) => [...prev, { role: 'user', content }]);
      setLoading(true);
      setError('');

      try {
        const res = await api.post(`/statements/${statementId}/chat`, {
          message: content,
          history,
        });
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: res.data?.reply || 'No response received.',
          },
        ]);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            'Something went wrong. Please try again.'
        );
      } finally {
        setLoading(false);
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    },
    [loading, statementId] // NO messages in deps — read via messagesRef
  );

  const label =
    bankName && accountNumber
      ? `${bankName} ···${accountNumber.slice(-4)}`
      : bankName || 'CA Assistant';

  return (
    <>
      {/* ── Floating trigger button (CSS hidden when expanded) ──────────── */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open CA Assistant"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-sky-500 shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-150 ${
          expanded ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <Sparkles className="w-6 h-6 text-white" />
      </button>

      {/* ── Single panel shell — CSS switches compact ↔ expanded ─────────── */}
      {open && (
        <div
          className={expanded ? expandedShell : compactShell}
          style={expanded ? {} : { height: '560px' }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0 bg-gradient-to-r from-violet-50 to-sky-50 dark:from-violet-950/40 dark:to-sky-950/40">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-sky-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-none">
                CA Assistant
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                {label}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-colors"
              >
                {expanded ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Expand className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  setExpanded(false);
                }}
                aria-label="Close"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body — empty state OR message list */}
          {messages.length === 0 && !loading ? (
            <EmptyState onSend={sendMessage} />
          ) : (
            <MessageList
              messages={messages}
              loading={loading}
              error={error}
              onDismissError={() => setError('')}
              messagesEndRef={messagesEndRef}
              expanded={expanded}
            />
          )}

          {/* Input — ONE instance in the entire file */}
          <InputBar
            onSend={sendMessage}
            loading={loading}
            textareaRef={textareaRef}
          />
        </div>
      )}
    </>
  );
}

export default memo(ChatPanel);
